import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { LayoutComponent } from '../../core/layout/layout.component';
import { RolesService } from '../../features/roles/services/roles.service';
import { ActivatedRoute } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { FormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import * as PermissionActions from './../../core/permissions/permission.action';
import { MatTabsModule } from '@angular/material/tabs';
import { ConfirmDialog } from '../../shared/components/confirm-dialog/confirm-dialog';
import { MatDialog } from '@angular/material/dialog';

@Component({
  selector: 'app-roles-management',
  standalone: true,
  imports: [CommonModule, LayoutComponent, FormsModule, MatTabsModule],
  templateUrl: './roles-management.component.html',
  styleUrl: './roles-management.component.scss',
})
export class RolesManagementComponent {
  isDropdownOpen = false;
  filter: any = {};
  modules: any[] = [];
  search: any;
  permissions: any[] = [];
  assignedModule: any;
  moduleId: any;
  profileId: any;
  selectedPermissionIds: any[] = [];
  isToggleOn: boolean = false;
  permissionNames: any[] = [];
  permissionMap: any = {};
  permissionMapId: any = {};
  // isChildDropdownOpen: boolean;
  dropdownState: { [key: number]: boolean } = {};
  mainDropdownState: { [key: number]: boolean } = {};
  childModules: any;
  permissionsState: { [key: number]: { isToggleOn: boolean } } = {}; // Track toggle state for each module
  profileName: any = '';
  members: any;
  filteredPermissionsMap: { [key: number]: any[] } = {};

  readonly dialog = inject(MatDialog);
  assignedPermissionIds: any;
  moduleDisplayNames: Record<number, string[]> = {};
  groupedModules: { [description: string]: any[] } = {};


  constructor(
    private rolesService: RolesService,
    private route: ActivatedRoute,
    private toastr: ToastrService,
    private store: Store,
  ) {}

  ngOnInit() {
    this.route.paramMap.subscribe((params) => {
      const id = params.get('id');
      if (id) {
        this.profileId = id;
        this.fetchProfile(Number(id));
        this.getPermissionsById(this.profileId);
      }
    });
  }

  toggleDropdown(event?: MouseEvent, moduleId?: number): void {
  event?.stopPropagation();
  if (moduleId == null) { return; }
  const wasOpen = !!this.mainDropdownState[moduleId];

  Object.keys(this.mainDropdownState).forEach((key: any) => {
    this.mainDropdownState[key] = false;
  });

  if (!wasOpen) {
    this.mainDropdownState[moduleId] = true;
    this.moduleId = moduleId;
    this.markAssignedPermissions(moduleId);  
  }
}

  fetchProfile(id: any) {
    this.rolesService.profile(id).subscribe({
      next: (data) => {
        this.members = data?.users;
        this.profileName = data?.profileName;
        // this.getModules();
      },
      error: (error) => {
        console.error(error, 'Error');
      },
    });
  }

  confirmActiveOrDeactivate(userId: any, status: any) {
    const dialogRef = this.dialog.open(ConfirmDialog, {
      data: {
        title: status === 0 ? 'Confirm Deactivation' : 'Confirm Activation',
        message:
          status === 0
            ? 'Are you sure you want to deactivate this user?'
            : 'Are you sure you want to activate this user?',
        confirmText: status === 0 ? 'Deactivate' : 'Activate',
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        this.activateOrDeactivateUser(userId, status);
      }
    });
  }

  activateOrDeactivateUser(userId: any, status: any) {
    this.rolesService.activateOrDeactivateUser(userId, status).subscribe({
      next: (data) => {
        if (status === 1) {
          this.toastr.success('User Enabled Successfully.');
        } else if (status === 0) {
          this.toastr.success('User Disabled Successfully.');
        }
        this.fetchProfile(Number(this.profileId));
      },
      error: (error) => {
        this.toastr.error(error, 'Error');
      },
    });
  }

  areAllPermissionsOff(moduleId: number): boolean {
    const mod = this.assignedModule.find((m: any) => m.moduleId === moduleId);
    if (!mod || !mod.permissions) return true;
    return mod.permissions.every((p: any) => !p.hasPermission);
  }

  checkUserType(array: any, roleId: number) {
    return array.filter((el: any) => el.parentModuleId !== roleId);
  }

  getPermissionsById(id: any) {
    if (id) {
      this.rolesService?.getModuleData(Number(id)).subscribe({
        next: (data) => {
          this.assignedModule = data;
          this.groupModulesByDescription();
          this.modules = this.assignedModule
            .map((mod: any) => ({
              moduleId: mod.moduleId,
              permissionIds: (mod.permissions || [])
                .filter((p: any) => p.hasPermission) // only active ones
                .map((p: any) => p.permissionId),
            }))
            .filter((m: any) => m.permissionIds.length);
          this.populatePermissionMap();
          // this.markAssignedPermissions();
        },
        error: (error) => {
          console.error(error, 'Error');
        },
      });
    }
  }

  groupModulesByDescription() {
    this.groupedModules = {};
  
    for (const module of this.assignedModule) {
      const desc = module.description || 'Others';
      if (!this.groupedModules[desc]) {
        this.groupedModules[desc] = [];
      }
      this.groupedModules[desc].push(module);
    }
  }

  populatePermissionMap() {
    this.moduleDisplayNames = {}; // Reset the display map

    this.assignedModule?.forEach((module: any) => {
      const permissions = module?.permissions || [];
      const activePermissions = permissions.filter(
        (perm: any) => perm.hasPermission === true
      );

      // Set toggle state
      this.permissionsState[module.moduleId] = {
        isToggleOn: activePermissions.length > 0,
      };

      // Set display names for button preview
      this.moduleDisplayNames[module.moduleId] = activePermissions.map(
        (perm: any) => perm.displayName
      );
    });
  }

  markAssignedPermissions(moduleId: number): void {
    if (!this.assignedModule?.length) {
      console.error('No assigned module data available.');
      return;
    }

    const module = this.assignedModule.find(
      (m: any) => m.moduleId === moduleId
    );
    if (!module) {
      console.error(`Module with ID ${moduleId} not found.`);
      return;
    }

    const allPermissions = module.permissions ?? [];

    /* ----------  1) full list for the popup  ---------- */
    this.permissions = allPermissions.map((p: any) => ({
      ...p,
      checked: !!p.hasPermission,
    }));

    /* ----------  2) names to show on the button  ---------- */
    this.assignedPermissionIds = allPermissions
      .filter((p: any) => p.hasPermission)
      .map((p: any) => p.displayName);

    /* ----------  3) ids of the checked perms  ---------- */
    this.selectedPermissionIds[moduleId] = this.permissions
      .filter((p: any) => p.checked)
      .map((p: any) => p.permissionId);
  }

  togglePermission(
    moduleId: number,
    permissionId: number,
    event: Event
  ): void {
    const checked = (event.target as HTMLInputElement).checked;
  
    let mod = this.modules.find(m => m.moduleId === moduleId);
    if (!mod) {
      mod = { moduleId, permissionIds: [] };
      this.modules.push(mod);
    }
  
    if (checked) {
      if (!mod.permissionIds.includes(permissionId)) {
        mod.permissionIds.push(permissionId);
      }
    } else {
      mod.permissionIds = mod.permissionIds.filter((id: any) => id !== permissionId);
    }
  
    const srcMod = this.assignedModule?.find((m: any) => m.moduleId === moduleId);
    
    console.log(' srcMod?.permissions', srcMod?.permissions);
    console.log(' srcMod', srcMod);
    const listIds: number[] =
      srcMod?.permissions
        ?.filter((p: any) => p.description === 'List') // label contains "List"
        .map((p: any) => p.permissionId) ?? [];
  
    /* ---------- 4. ensure every non‑List selection brings its List ID(s) ---------- */
    const hasNonList = mod.permissionIds.some((id: any) => !listIds.includes(id));
    if (hasNonList) {
      listIds.forEach(id => {
        if (!mod.permissionIds.includes(id)) {
          mod.permissionIds.push(id);
        }
      });
    }
  
    /* ---------- 5. sync hasPermission flags in the source snapshot ---------- */
    if (srcMod?.permissions) {
      srcMod.permissions.forEach((p: any) => { 
        if (p.permissionId === permissionId) {
          p.hasPermission = checked;
        }
        // Force List IDs ON when other perms exist
        if (listIds.includes(p.permissionId) && hasNonList) {
          p.hasPermission = true;
        }
      });
    }
  
    /* ---------- 6. prune empty modules (optional) ---------- */
    if (mod.permissionIds.length === 0) {
      this.modules = this.modules.filter(m => m.moduleId !== moduleId);
    }
  }
  
  // toggleAllPermissions(event: Event, moduleId: number): void {
  //   const isChecked = (event.target as HTMLInputElement).checked;
  
  //   /* 1️⃣  remember the switch state for the template */
  //   this.permissionsState[moduleId] = { isToggleOn: isChecked };
  
  //   /* 2️⃣  get the full, authoritative list of ids for this module */
  //   const src = this.assignedModule?.find((m: any) => m.moduleId === moduleId);
  //   const ids: number[] = src?.permissions?.map((p: any) => p.permissionId) ?? [];
  
  //   /* 3️⃣  flip the checkboxes just for this module */
  //   this.permissions = this.permissions.map((p: any) =>
  //     p.moduleId === moduleId ? { ...p, checked: isChecked } : p
  //   );
  
  //   /* 4️⃣  update / create the payload entry */
  //   let mod = this.modules.find(m => m.moduleId === moduleId);
  //   if (!mod) {
  //     mod = { moduleId, permissionIds: [] };
  //     this.modules.push(mod);
  //   }
  //   mod.permissionIds = isChecked ? [...ids] : [];
  
  //   // auto‑add 4 if any other permission is on
  //   if (isChecked && ids.length && !mod.permissionIds.includes(4)) {
  //     mod.permissionIds.push(4);
  //   }
  
  //   /* 5️⃣  keep the source snapshot in sync so reopen shows the new state */
  //   src?.permissions?.forEach((p: any) => (p.hasPermission = isChecked));
  
  //   /* 6️⃣  update any legacy flat list (if still used elsewhere) */
  //   this.selectedPermissionIds = isChecked
  //     ? Array.from(new Set([...this.selectedPermissionIds, ...mod.permissionIds]))
  //     : this.selectedPermissionIds.filter(id => !ids.includes(id));
  
  //   this.onApply();  // whatever follow‑up you already perform
  // }

  toggleAllPermissions(event: Event, moduleId: number): void {
    const isChecked = (event.target as HTMLInputElement).checked;
  
    // 1️⃣ Remember toggle state for UI tracking
    this.permissionsState[moduleId] = { isToggleOn: isChecked };
  
    // 2️⃣ Get the source module and all permission IDs
    const src = this.assignedModule?.find((m: any) => m.moduleId === moduleId);
    const allIds: number[] = src?.permissions?.map((p: any) => p.permissionId) ?? [];
  
    // 3️⃣ Flip the checkboxes for this module in the flat permissions list
    this.permissions = this.permissions.map((p: any) =>
      p.moduleId === moduleId ? { ...p, checked: isChecked } : p
    );
  
    // 4️⃣ Update or create the corresponding entry in `modules`
    let mod = this.modules.find(m => m.moduleId === moduleId);
    if (!mod) {
      mod = { moduleId, permissionIds: [] };
      this.modules.push(mod);
    }
  
    mod.permissionIds = isChecked ? [...allIds] : [];
  
    // ✅ Dynamically add the 'List' permission ID if needed
    const listIds: number[] = src?.permissions
      ?.filter((p: any) => p.description === 'List')
      .map((p: any) => p.permissionId) ?? [];
  
    const hasNonList = mod.permissionIds.some((id: any) => !listIds.includes(id));
    if (isChecked && hasNonList) {
      listIds.forEach(id => {
        if (!mod!.permissionIds.includes(id)) {
          mod!.permissionIds.push(id);
        }
      });
    }
  
    // 5️⃣ Sync the snapshot so the UI reflects the current state
    src?.permissions?.forEach((p: any) => {
      p.hasPermission = isChecked;
  
      // Force 'List' permission to true if any non-list permission is on
      if (listIds.includes(p.permissionId) && hasNonList) {
        p.hasPermission = true;
      }
    });
  
    // 6️⃣ Update any flat list of selected IDs
    this.selectedPermissionIds = isChecked
      ? Array.from(new Set([...this.selectedPermissionIds, ...mod.permissionIds]))
      : this.selectedPermissionIds.filter(id => !allIds.includes(id));
  
    this.onApply();  // Any follow-up logic
  }
  
  
  onApply() {

    const modules = {
      profileId: Number(this.profileId),
      modules: this.modules,
    };

    console.log('modules', modules);

    this.rolesService.assignBulkPermissions(modules).subscribe({
      next: (data) => {
        if (data) {
          this.toastr.success('Permission updated successfully', 'Success');

          // Update localStorage rolesAndPermision
          const rolesAndPermision = JSON.parse(
            localStorage.getItem('rolesAndPermision') || '[]'
          );
          const moduleIndex = rolesAndPermision.findIndex(
            (module: any) => module.moduleId === this.moduleId
          );

          if (moduleIndex !== -1) {
            rolesAndPermision[moduleIndex].permissions =
              this.selectedPermissionIds.map((id: number) => {
                return this.permissions.find(
                  (permission: any) => permission.id === id
                );
              });
            localStorage.setItem(
              'rolesAndPermision',
              JSON.stringify(rolesAndPermision)
            );
          }
          if(this.mainDropdownState[this.moduleId]) {
            this.mainDropdownState[this.moduleId] = !this.mainDropdownState[this.moduleId];
          }
          const permsDict = Object.fromEntries(
            this.modules.map(m => [m.moduleId, m.permissionIds])
            
          );
          for (const groupKey in this.groupedModules) {
            for (const module of this.groupedModules[groupKey]) {
              if (this.areAllPermissionsOff(module.moduleId)) {
                this.permissionsState[module.moduleId] = { isToggleOn: false };
              } else {
                this.permissionsState[module.moduleId] = { isToggleOn: true };
              }
            }
          }
          console.log('permsDict', permsDict);
          
          this.store.dispatch(PermissionActions.setPermissions({ permissions: permsDict }));
          this.getPermissionsById(this.profileId);
        }
      },
      error: (error) => {
        this.toastr.error(error, 'Error');
        this.modules = [];
      },
    });
  }
}
